import { Controller } from "@hotwired/stimulus"

/**
 * iot--location-compare-toggle — additive reveal for the reports'
 * location-comparison mode, mirroring the period-compare pattern: an
 * "Add comparison location" button reveals the Location B panel (and hides
 * itself), and the panel carries its own × remove control. A visually-hidden
 * `compare_locations` checkbox is the REAL form field — the backend gates the
 * mode on it alone. Removing also disables the panel's fieldset, so the next
 * Apply submits no location_b[...] params and the URL scrubs itself clean
 * (stale params that do arrive are inert without the checkbox anyway).
 * Single slot on purpose: v1 compares the main cascade selection
 * against ONE extra location — compare_toggle_controller.js's multi-slot
 * machinery is the template if more slots land later.
 *
 * Mutual exclusivity with the period mode is BEST-EFFORT at this layer —
 * the authoritative rule lives server-side: period comparison
 * deterministically wins when both submit.
 */
export default class extends Controller {
  static targets = ["checkbox", "addButton", "panel", "fields"]

  connect() {
    // Seed from the peer's server-rendered checkbox rather than assuming
    // inactive: Stimulus wires a context's data-action listeners only when
    // that context connects, so the FIRST controller's connect-time dispatch
    // fires before the second one is listening and is lost — and the
    // flip-only guard below never re-announces an unchanged state. Reading
    // the DOM makes connect order irrelevant.
    this.peerActive =
      this.element.querySelector(
        "[data-iot--compare-toggle-target~='checkbox']",
      )?.checked === true
    this.sync()
  }

  add() {
    this.checkboxTarget.checked = true
    this.sync()
  }

  remove() {
    this.checkboxTarget.checked = false
    this.sync()
  }

  // Mutual exclusivity with period comparison — see
  // compare_toggle_controller.js: each controller hides its own Add button
  // while the peer mode is active.
  onPeerChanged(event) {
    this.peerActive = event.detail.active
    this.sync()
  }

  // Also bound to the checkbox's own change event so keyboard/AT toggling
  // the real field keeps the button + panel in step.
  sync() {
    const active = this.checkboxTarget.checked
    this.panelTarget.hidden = !active
    // Disabled fields never reach FormData, so an inactive slot's
    // location_b[...] params vanish from the next Apply's URL. The fieldset
    // overlays the cascade's own per-select disabled states without
    // touching them, so re-adding restores the previous selection intact.
    if (this.hasFieldsTarget) this.fieldsTarget.disabled = !active
    this.addButtonTarget.hidden = active || this.peerActive === true
    // Flip-only dispatch — an unconditional one would ping-pong the two
    // controllers into an infinite synchronous event loop.
    if (this.lastDispatchedActive !== active) {
      this.lastDispatchedActive = active
      this.dispatch("changed", { detail: { active } })
    }
  }
}
